import { format } from "date-fns";
import React, { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useGetRecordedVideoDistributionRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface VideosRecordedRawDataProps {}

interface VideosRecordedRawDataType {
  clientName: string;
  rbtName: string;
  uploadingVideosCount: number;
  scheduledVideosCount: number;
  processingVideosCount: number;
  failedVideosCount: number;
  completedVideosCount: number;
  totalVideosRecorded: number;
}

export const VideosRecordedRawData: React.FC<VideosRecordedRawDataProps> = () => {
  const { selectedRBTDates, selectedBCBA: selectedBCBAList } = useVideoAnalytics();

  const [selectedRBT, setSelectedRBT] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedRBTDates);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedRBT, selectedClient]);

  const { data, isFetching } = useGetRecordedVideoDistributionRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      rbtProviderIds: selectedRBT.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
    },
    {
      queryKey: ["getRecordedVideoDistributionRawData", dates, selectedRBT, pageNo, pageSize, selectedClient],
    },
  );
  const columns: Column<VideosRecordedRawDataType>[] = [
    {
      title: "Client",
      key: "clientName",
      dataIndex: "clientName",
      render: (value) => <span className="ph-no-capture">{(value as string) || "-"}</span>,
    },
    {
      title: "Uploading",
      key: "uploadingVideosCount",
      dataIndex: "uploadingVideosCount",
      render: (_, record) => record.uploadingVideosCount ?? 0,
    },
    {
      title: "Processing",
      key: "scheduledVideosCount",
      dataIndex: "scheduledVideosCount",
      render: (_, record) => record.scheduledVideosCount + record.processingVideosCount,
    },
    {
      title: "Failed",
      key: "failedVideosCount",
      dataIndex: "failedVideosCount",
      render: (_, record) => record.failedVideosCount ?? 0,
    },
    {
      title: "Successful",
      key: "completedVideosCount",
      dataIndex: "completedVideosCount",
      render: (_, record) => record.completedVideosCount ?? 0,
    },
    {
      title: "Total Videos",
      key: "totalVideosRecorded",
      dataIndex: "totalVideosRecorded",
      render: (_, record) => record.totalVideosRecorded ?? 0,
    },
    {
      title: "RBT",
      key: "rbtName",
      dataIndex: "rbtName",
      render: (value) => value || "-",
    },
  ];
  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedRBTs={selectedRBT}
        setSelectedRBTs={setSelectedRBT}
        selectedClients={selectedClient}
        setSelectedClients={setSelectedClient}
        showBCBAFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.getRecordedVideoDistributionRawData?.rawData
            ? (data.getRecordedVideoDistributionRawData?.rawData as VideosRecordedRawDataType[])
            : []
        }
        columns={columns}
        loading={isFetching}
        totalItemCount={data?.getRecordedVideoDistributionRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
